import styled, { css } from 'styled-components'
import { Heading, Paragraph } from '../../atoms/Typography/Typography.styles'

import { Icon as StyledIcon } from '../../atoms/Icon/Icon.styles'

import { 
  BoxShapeProps, 
  BoxContentProps, 
  BoxTextContentProps, 
  BoxImageWrapperProps, 
  BoxTextSecondaryProps, 
  BoxButtonWrapperProps, 
  BoxActionsProps, 
  BoxWrapperProps, 
  BoxFeaturesListProps, 
  BoxFeaturesListItemProps 
} from './Box.types'

export const BoxShape = styled.div<BoxShapeProps>`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 50%;
  left: 50%;
  opacity: 0.15;
  pointer-events: none;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ theme }) => theme.mediaQuery.md(`
    justify-content: flex-end;
  `)}

  ${({ theme }) => theme.mediaQuery.lg(`
    justify-content: center;
  `)}

  > svg {
    height: 120%;
    width: auto;
  }
`

export const BoxContent = styled.div<BoxContentProps>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.rem(16)};
  align-items: ${({ $alignment }) => $alignment === 'center' ? 'center' : 'flex-start'};
  align-self: stretch;
  padding: ${({ theme }) => theme.rem(24)};
  flex-grow: 1;
  justify-content: space-between;

  ${({ theme, $alignment }) =>
    theme.mediaQuery.lg(`
    max-width: ${$alignment === 'left' ? 'unset' : theme.rem(750)};
    margin: 0 auto 0 0;
    padding: ${theme.rem(40)};
  `)}
`

export const BoxTextContent = styled.div<BoxTextContentProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.rem(8)};
  align-self: stretch;
  ${({ theme, $alignment }) =>
    theme.mediaQuery.lg(`
      text-align: ${$alignment === 'center' ? 'center' : 'left'};
      align-items: ${$alignment === 'center' ? 'center' : 'flex-start'};
  `)}
`

export const BoxImageWrapper = styled.div<BoxImageWrapperProps>`
  width: 100%;
  min-height: ${({ theme }) => theme.rem(200)};
  position: relative;
  display: flex;
  margin: auto auto 0;

  ${({ theme }) =>
    theme.mediaQuery.md(`
    max-width: ${theme.rem(1060)};
  `)}

  img {
    position: relative!important;
    object-fit: cover;
    object-position: center;
  }
`

export const BoxTextSecondary = styled.div<BoxTextSecondaryProps>`
  width: 100%;
  > * {
    color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].surface.text.base.primary};
  }
`

export const BoxButtonWrapper = styled.div<BoxButtonWrapperProps>`

  a {
    width: 100%;
  }
`

export const BoxActions = styled.div<BoxActionsProps>`
  width: 100%;
  gap: ${({ theme }) => theme.rem(8)};
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.mediaQuery.lg(`
    flex-direction: row;
    gap: ${theme.rem(24)};
    align-items: center;
    flex-wrap: wrap;
  `)}

  a,
  button {
    width: 100%;

    ${({ theme }) => theme.mediaQuery.md(`
      width: auto;
    `)}
  }
`

export const BoxWrapper = styled.div<BoxWrapperProps>`
  display: flex;
  width: 100%;
  height: 100%;
  min-height: ${({ theme }) => theme.rem(200)};
  overflow: hidden;
  flex-direction: ${({ $withImage }) => ($withImage ? 'column' : 'row')};
  position: relative;
  color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].surface.text.base.primary};
  justify-content: space-between;
  
  ${Paragraph},
  ${Heading} {
    color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].surface.text.base.primary};
  }


  span.highlighted {
    background-color: transparent!important;
    color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].surface.text.accent.base};
  }

  ${({ theme, $isBentoBox, $withImage }) =>
    $isBentoBox &&
    css`
      border-radius: ${theme.rem(16)};

      ${!$withImage &&
      theme.mediaQuery.xl(`
        flex-direction: row;
      `)}
    `}

  background-color: ${({theme, colorScheme }) => theme.colorScheme[`${colorScheme}`].surface.inside};

  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.rem(24)};
  }
  
  ${BoxImageWrapper} {
    aspect-ratio: 4 / 4;
    overflow: hidden;
    margin: 0 auto;
  

    ${({ theme }) => theme.mediaQuery.xl(`
      border-radius: ${theme.rem(8)};
    `)}
  }
  
  ${({ theme, variant }) => variant === 'full-centered' && css`
    ${BoxImageWrapper} {
      max-height: ${theme.rem(500)};
    }
  `};

  ${({ theme, variant }) => variant === 'full-centered' && theme.mediaQuery.lg(`
    ${BoxTextSecondary} {
      text-align: center;
    }

    ${BoxTextContent} {
      max-width: ${theme.rem(920)};
      align-items: center;
      text-align: center;
      margin: 0 auto;
      padding: ${theme.rem(0, 80)};
    }

    ${BoxActions} {
      justify-content: center;
    }

    ${BoxImageWrapper} {
      aspect-ratio: 2 / 1;
      max-width: initial;
      width: 100%;
      padding: ${theme.rem(0, 60, 60)};
      overflow: hidden;
      
      img {
        border-radius: ${theme.rem(8)};
      }
    }

    ${BoxContent} {
      margin: 0;
      padding: ${theme.rem(60)};
    }
`)} 

${({ theme, variant, $withImage }) => variant === 'full-horizontal' && theme.mediaQuery.md(`
    flex-direction: row;
    align-items: center;

    ${BoxImageWrapper} {
      aspect-ratio: 4 / 5;
      margin: ${theme.rem(40)};
      border-radius: ${theme.rem(24)};
      overflow: hidden;
      
      img {
        object-fit: cover;
      }
    }

    ${BoxContent} {
      margin: 0;
      padding-left: ${theme.rem(100)};
      padding-right: ${theme.rem(100)};
      padding-top: ${theme.rem(40)};
      padding-bottom: ${theme.rem(40)};
      justify-content: center;

      ${!$withImage && `
        width: 100%;
      `}
    }
  `)}

  ${({ theme, variant }) => variant === 'double-equal' && theme.mediaQuery.md(`
    justify-content: space-between;

    ${BoxImageWrapper} {
      // aspect-ratio: 16 / 9;
      // max-height: ${theme.rem(320)};
      max-height: 100%;
      height: 100%;
      border-radius: 0;

      img {
        object-fit: cover;
        object-position: center;
      }
    }
  `)}

  ${({ theme, variant }) => variant === 'double-biggerLeft' && theme.mediaQuery.md(`
    justify-content: space-between;

    ${BoxImageWrapper} {
      aspect-ratio: initial;
      max-height: initial;
      height: 100%;
      border-radius: 0;

      img {
        object-fit: cover;
        object-position: center;
      }
    }
  `)}

  ${({ theme, variant }) => variant === 'double-biggerRight' && theme.mediaQuery.md(`
    justify-content: space-between;

    ${BoxImageWrapper} {
      aspect-ratio: initial;
      max-height: initial;
      height: 100%;
      border-radius: 0;

      img {
        object-fit: cover;
        object-position: center;
      }
    }
  `)}

  ${({ theme, variant }) => variant === 'triple-equal' && theme.mediaQuery.md(`
    justify-content: space-between;

    ${BoxWrapper} {
      justify-content: initial;
    }

    ${BoxContent} {
      min-height: initial;
      justify-content: space-between;
      flex-grow: 1;
    }

    ${BoxImageWrapper} {
      aspect-ratio: initial;
      height: ${theme.rem(300)};
      border-radius: 0;

      img {
        object-fit: cover;
        object-position: center;
      }
    }
  `)}

  ${({ theme, variant }) => variant === 'triple-equal' && theme.mediaQuery.lg(`
    ${BoxImageWrapper} {
      height: ${theme.rem(200)};
    }
  `)}
  
  ${({ theme, variant }) => variant === 'triple-equal' && theme.mediaQuery.xl(`
    ${BoxImageWrapper} {
      height: ${theme.rem(300)};
    }
  `)}
`

export const BoxFeaturesList = styled.div<BoxFeaturesListProps>`
  padding: ${({ theme }) => theme.rem(8,0)};
`

export const BoxFeaturesListItem = styled.div<BoxFeaturesListItemProps>`
  display: flex;
  align-items: center;

  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.rem(8)};
  }

  ${StyledIcon} {
    margin-right: ${({ theme }) => theme.rem(8)};
  }
`